import { createClient } from '@supabase/supabase-js';

// Read from the build-time environment. These were previously hardcoded, which
// meant the compiled bundle kept talking to the old backend no matter what the
// deployment environment said. Falls back to the historical Supabase project so
// an un-configured local build still works.
const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'https://ctfbfymdoovkwodavmvs.supabase.co';
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ?? 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN0ZmJmeW1kb292a3dvZGF2bXZzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM4MjE2MjQsImV4cCI6MjA2OTM5NzYyNH0.52p3kd1xP2pbpDN0TL0qI1xQ8cdmusxiCPo40rxTMCk';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Database table names
export const TABLES = {
  USERS: 'users',
  USER_SESSIONS: 'user_sessions',
  CHAT_ROOMS: 'chat_rooms',
  CHAT_MEMBERS: 'chat_members',
  MESSAGES: 'messages',
  TRANSLATIONS: 'translations',
  USER_BLOCKS: 'user_blocks',
  USER_REPORTS: 'user_reports',
  RATE_LIMITS: 'rate_limits',
  ACTIVE_CONNECTIONS: 'active_connections',
} as const;

// Helper functions for common operations
export const supabaseHelpers = {
  // User operations
  async getUser(uid: string) {
    const { data, error } = await supabase
      .from(TABLES.USERS)
      .select('*')
      .eq('uid', uid)
      .single();

    // If no user found, return null instead of throwing error
    if (error && error.code === 'PGRST116') {
      return null;
    }

    if (error) throw error;
    return data;
  },

  async createUser(userData: {
    uid: string;
    display_name: string;
    email?: string;
    preferred_language?: string;
    pronouns?: string | null;
  }) {
    const { data, error } = await supabase
      .from(TABLES.USERS)
      .upsert([userData], { onConflict: 'uid' })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateUser(uid: string, updates: Partial<{
    display_name: string;
    preferred_language: string;
    is_online: boolean;
    last_seen: string;
    email: string;
    auth_provider: string;
    is_anonymous: boolean;
    upgraded_at: string;
    pronouns: string | null;
  }>) {
    const { data, error } = await supabase
      .from(TABLES.USERS)
      .update(updates)
      .eq('uid', uid)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Chat room operations
  async createChatRoom(roomData: {
    room_id: string;
    name: string;
    description?: string;
    type: string;
    created_by: string;
    invite_code: string;
  }) {
    const { data, error } = await supabase
      .from(TABLES.CHAT_ROOMS)
      .insert([roomData])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getChatRooms(userId: string) {
    const { data, error } = await supabase
      .from(TABLES.CHAT_ROOMS)
      .select(`
        *,
        chat_members!inner(user_id)
      `)
      .eq('chat_members.user_id', userId)
      .eq('is_active', true);

    if (error) throw error;
    return data;
  },

  async joinChatRoom(roomId: string, userId: string, invitedBy?: string) {
    const { data, error } = await supabase
      .from(TABLES.CHAT_MEMBERS)
      .insert([{
        room_id: roomId,
        user_id: userId,
        invited_by: invitedBy,
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Message operations
  async saveMessage(messageData: {
    message_id: string;
    room_id: string;
    sender_id: string;
    content: string;
    message_type?: string;
  }) {
    const { data, error } = await supabase
      .from(TABLES.MESSAGES)
      .insert([messageData])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getMessages(roomId: string, limit = 50) {
    const { data, error } = await supabase
      .from(TABLES.MESSAGES)
      .select('*')
      .eq('room_id', roomId)
      .eq('is_deleted', false)
      .order('timestamp', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data.reverse(); // Return in chronological order
  },

  // Translation operations
  async saveTranslation(translationData: {
    message_id: string;
    target_language: string;
    translated_content: string;
    confidence?: number;
    provider?: string;
  }) {
    const { data, error } = await supabase
      .from(TABLES.TRANSLATIONS)
      .upsert([translationData], {
        onConflict: 'message_id,target_language'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getTranslation(messageId: string, targetLanguage: string) {
    const { data, error } = await supabase
      .from(TABLES.TRANSLATIONS)
      .select('*')
      .eq('message_id', messageId)
      .eq('target_language', targetLanguage)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return data;
  },

  // Connection tracking
  async trackConnection(connectionData: {
    user_id: string;
    socket_id: string;
    room_id?: string;
  }) {
    const { data, error } = await supabase
      .from(TABLES.ACTIVE_CONNECTIONS)
      .upsert([connectionData], {
        onConflict: 'socket_id'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removeConnection(socketId: string) {
    const { error } = await supabase
      .from(TABLES.ACTIVE_CONNECTIONS)
      .delete()
      .eq('socket_id', socketId);

    if (error) throw error;
  },

  async updateConnectionPing(socketId: string) {
    const { error } = await supabase
      .from(TABLES.ACTIVE_CONNECTIONS)
      .update({ last_ping: new Date().toISOString() })
      .eq('socket_id', socketId);

    if (error) throw error;
  },
}; 